import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box,Tab, Tabs, Typography, Stack, useTheme } from '@mui/material'
import { useParams, useOutletContext, Navigate } from 'react-router-dom'
import { useNavigate } from 'router'

import Status from 'components/global/Status'

import {
  availableToColor,
  availableToBgColor,
  availableToTranslationKey,
} from '../../../helpers'
import { useUser } from 'store'
import { StaticMap } from 'components/Cartography'
import { ResponsiveSideContent } from 'components-shared'
import CardPreview, {
  CardBlockInfo,
  CardHeader
} from 'packages/ui/CardPreview'

import Infos from './Infos'
import Speedtest from './Speedtest'

const Header = ({ site }) => {
  const { t } = useTranslation()
  const theme = useTheme()

  return (
    <CardHeader sx={{ width: '100%', minWidth: 0 }}>
      <Stack direction="column" alignItems="center" gap={1.5} width="100%" minWidth={0}>
        <Status
          status={t(
            availableToTranslationKey(
              site.equipment ? site.equipment.available_level : 'down',
            ),
          )}
          color={availableToColor(
            site.equipment ? site.equipment.available_level : 'down',
          )}
          bgcolor={availableToBgColor(
            site.equipment ? site.equipment.available_level : 'down',
          )}
          isUppercase={false}
        />
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: 18,
            color: theme.palette.grey[800],
            maxWidth: '100%',
            textAlign: 'center',
            wordBreak: 'break-word'
          }}
        >
          {site.name}
        </Typography>
        <Box sx={{ width: '100%', minWidth: 0 }}>
          <StaticMap site={site} positions={[site.position]} height={192} minWidth={0}/>
        </Box>
        <Stack flex={1} width={1}>
          <CardBlockInfo
            title={t('audin_document.card_details.address')}
            subtitle={`${
              site.address.street_number ? site.address.street_number : ''
            } ${site.address.street_name}, ${site.address.zipcode} ${
              site.address.city
            }`}
          />
        </Stack>
      </Stack>
    </CardHeader>
  )
}



const Card = () => {
  const { focusId } = useParams()
  const [selectedTab, setSelectedTab] = useState('infos')
  const { sites } = useOutletContext()
  const [user] = useUser()
  const site = useMemo(() => {
    return focusId ? sites.find(({ sf_id }) => sf_id === focusId) : null
  }, [sites, focusId])
  const { t } = useTranslation()
  const navigate = useNavigate()
  const handleClose = () => navigate({ pathname: '..' })

  const onChangeTab = (event, newValue) => {
    setSelectedTab(newValue)
  }
  if (focusId && !site) {
    return <Navigate to={'/monitoring/sites'} />
  }
  return (
    <ResponsiveSideContent onClose={handleClose} style={{ marginTop: 0 }}>
      <CardPreview
        title={t('ui_elements.card_title')}
        onClose={handleClose}
        padding={2}
      >
        <Box 
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            gap: 1.5,
            pb: 2
          }}
        >
          {site && <Header site={site} />}
          <Tabs
            value={selectedTab}
            onChange={onChangeTab}
            sx={{
              padding: 0,
              fontWeight: 300,
              mt: 1.5
            }}>
            <Tab component="div" label={t('supervision.card.tab.infos')} value="infos" />
            {(user.admin || user.roles.speedtest) && (
              <Tab
                component="div"
                label={t('supervision.card.tab.speedtest')}
                value="speedtest"
              />
            )} 
          </Tabs>
          {selectedTab === 'infos' && <Infos site={site} />}
          {selectedTab === 'speedtest' && <Speedtest site={site} />}
        </Box>
      </CardPreview>
    </ResponsiveSideContent>
  )
  
}

export default Card
